import {z} from 'zod';

import {BaseVoidResponseSchema} from '@dms/api/shared';

// ====================
// AI Vehicle Service Intervals — status
// ====================

export const AiVehicleServiceIntervalsStatusSchema = z.enum(['processing', 'completed', 'failed']);

export type AiVehicleServiceIntervalsStatus = z.infer<typeof AiVehicleServiceIntervalsStatusSchema>;

// ====================
// Service Interval Plan (computed on the backend, rendered by the design)
// ====================

export const ServiceIntervalPlanStepBucketSchema = z.enum(['dueNow', 'upcoming', 'excluded']);

export type ServiceIntervalPlanStepBucket = z.infer<typeof ServiceIntervalPlanStepBucketSchema>;

export const ServiceIntervalPlanStepPartSchema = z.object({
  partNumber: z.string(),
  price: z.number().nullable(),
  compatibilityCriteria: z.string().nullable(),
  type: z.string().nullable(),
});

export type ServiceIntervalPlanStepPart = z.infer<typeof ServiceIntervalPlanStepPartSchema>;

export const ServiceIntervalPlanStepSchema = z.object({
  workName: z.string(),
  criteria: z.string().nullable(),
  workPos: z.string().nullable(),
  itemMpId: z.number().int().nullable(),
  workId: z.number().int().nullable(),
  intervalMileage: z.number().int().nullable(),
  intervalMonths: z.number().int().nullable(),
  normTimeHours: z.number().nullable(),
  bucket: ServiceIntervalPlanStepBucketSchema,
  remainingMileage: z.number().int().nullable(),
  exclusionReason: z.string().nullable(),
  requiresClarification: z.boolean(),
  parts: z.array(ServiceIntervalPlanStepPartSchema),
});

export type ServiceIntervalPlanStep = z.infer<typeof ServiceIntervalPlanStepSchema>;

export const ServiceIntervalPlanMetricsSchema = z.object({
  dueNowCount: z.number().int(),
  nextServiceMileage: z.number().int().nullable(),
  estimatedWorkHoursNow: z.number().nullable(),
  excludedCount: z.number().int(),
});

export type ServiceIntervalPlanMetrics = z.infer<typeof ServiceIntervalPlanMetricsSchema>;

export const ServiceIntervalPlanCommentarySchema = z.object({
  summary: z.array(z.string()),
  actions: z.array(z.string()),
});

export type ServiceIntervalPlanCommentary = z.infer<typeof ServiceIntervalPlanCommentarySchema>;

export const ServiceIntervalPlanPartsCalloutSchema = z.object({
  priceRangeFrom: z.number().nullable(),
  priceRangeTo: z.number().nullable(),
  compatibleVariantsCount: z.number().int().nullable(),
  requiredPrCodes: z.array(z.string()),
});

export type ServiceIntervalPlanPartsCallout = z.infer<typeof ServiceIntervalPlanPartsCalloutSchema>;

export const ServiceIntervalPlanSchema = z.object({
  calculatedForMileage: z.number().int(),
  intervalType: z.string().nullable(),
  selectedItemsCount: z.number().int(),
  totalItemsCount: z.number().int(),
  metrics: ServiceIntervalPlanMetricsSchema,
  commentary: ServiceIntervalPlanCommentarySchema.nullable(),
  partsCallout: ServiceIntervalPlanPartsCalloutSchema.nullable(),
  steps: z.array(ServiceIntervalPlanStepSchema),
});

export type ServiceIntervalPlan = z.infer<typeof ServiceIntervalPlanSchema>;

// ====================
// Service Interval Suggestions (per-category "AI návrh" column)
// ====================

export const ServiceIntervalSuggestionSchema = z.object({
  nextDate: z.string().nullable(),
  nextMileage: z.number().int().nullable(),
  afterMonths: z.number().int().nullable(),
  afterMileage: z.number().int().nullable(),
});

export type ServiceIntervalSuggestion = z.infer<typeof ServiceIntervalSuggestionSchema>;

export const ServiceIntervalSuggestionsSchema = z.object({
  serviceInspection: ServiceIntervalSuggestionSchema.nullable(),
  engineOil: ServiceIntervalSuggestionSchema.nullable(),
  gearboxOil: ServiceIntervalSuggestionSchema.nullable(),
  fourByFourOilDistributor: ServiceIntervalSuggestionSchema.nullable(),
  timingBelt: ServiceIntervalSuggestionSchema.nullable(),
  brakeFluid: ServiceIntervalSuggestionSchema.nullable(),
  coolantFluid: ServiceIntervalSuggestionSchema.nullable(),
});

export type ServiceIntervalSuggestions = z.infer<typeof ServiceIntervalSuggestionsSchema>;

// ====================
// The record (Generate / Generate latest response)
// ====================

export const AiVehicleServiceIntervalsResponseBodySchema = z.object({
  id: z.string().uuid(),
  vehicleId: z.string().uuid(),
  status: AiVehicleServiceIntervalsStatusSchema,
  generatedAt: z.string(),
  // Set when the record's suggestions have been applied to vehicle.serviceIntervals; `null` while
  // the plan is only generated. Drives whether the "Apply AI suggestions" action is offered.
  appliedAt: z.string().nullable(),
  calculatedForMileage: z.number().int().nullable(),
  annualMileageUsed: z.number().int().nullable(),
  plan: ServiceIntervalPlanSchema.nullable(),
  suggestions: ServiceIntervalSuggestionsSchema.nullable(),
});

export type AiVehicleServiceIntervalsResponseBody = z.infer<
  typeof AiVehicleServiceIntervalsResponseBodySchema
>;

// ====================
// Generate (synchronous): POST /dms/v1/vehicle/{vehicleId}/ai-service-intervals
// ====================

export const GenerateAiVehicleServiceIntervalsRequestBodySchema = z.object({
  currentMileage: z.number().int(),
});

export type GenerateAiVehicleServiceIntervalsRequestBody = z.infer<
  typeof GenerateAiVehicleServiceIntervalsRequestBodySchema
>;

export const GenerateAiVehicleServiceIntervalsApiArgSchema = z.object({
  vehicleId: z.string().uuid(),
  body: GenerateAiVehicleServiceIntervalsRequestBodySchema,
});

export type GenerateAiVehicleServiceIntervalsApiArg = z.infer<
  typeof GenerateAiVehicleServiceIntervalsApiArgSchema
>;

export const GenerateAiVehicleServiceIntervalsApiResponseSchema =
  AiVehicleServiceIntervalsResponseBodySchema;

export type GenerateAiVehicleServiceIntervalsApiResponse = z.infer<
  typeof GenerateAiVehicleServiceIntervalsApiResponseSchema
>;

// ====================
// Generate latest: GET /dms/v1/vehicle/{vehicleId}/ai-service-intervals/latest
// ====================

export const GetLatestAiVehicleServiceIntervalsApiArgSchema = z.object({
  vehicleId: z.string().uuid(),
});

export type GetLatestAiVehicleServiceIntervalsApiArg = z.infer<
  typeof GetLatestAiVehicleServiceIntervalsApiArgSchema
>;

export const GetLatestAiVehicleServiceIntervalsApiResponseSchema =
  AiVehicleServiceIntervalsResponseBodySchema;

export type GetLatestAiVehicleServiceIntervalsApiResponse = z.infer<
  typeof GetLatestAiVehicleServiceIntervalsApiResponseSchema
>;

// ====================
// Apply: POST /dms/v1/vehicle/{vehicleId}/ai-service-intervals/{aiVehicleServiceIntervalId}/apply
// Writes the record's suggestions into vehicle.serviceIntervals; returns 204.
// ====================

export const ApplyAiVehicleServiceIntervalApiArgSchema = z.object({
  aiVehicleServiceIntervalId: z.string().uuid(),
  vehicleId: z.string().uuid(),
});

export type ApplyAiVehicleServiceIntervalApiArg = z.infer<
  typeof ApplyAiVehicleServiceIntervalApiArgSchema
>;

export const ApplyAiVehicleServiceIntervalApiResponseSchema = BaseVoidResponseSchema;

export type ApplyAiVehicleServiceIntervalApiResponse = z.infer<
  typeof ApplyAiVehicleServiceIntervalApiResponseSchema
>;
